import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader, PageBody } from "@/components/app/page-header";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, User as UserIcon, Loader2, Trash2 } from "lucide-react";
import { deleteMyAccount } from "@/lib/account.functions";

export const Route = createFileRoute("/_authenticated/app/settings")({
  component: SettingsPage,
});

function SettingsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState(false);

  const onDelete = async () => {
    if (!confirm(t("pages.settings.deleteConfirm"))) return;
    setDeleting(true);
    try {
      const r = await deleteMyAccount();
      if (!r.ok) throw new Error(r.error);
      await supabase.auth.signOut();
      toast.success(t("pages.settings.accountDeleted"));
      navigate({ to: "/" });
    } catch (e: any) {
      setDeleting(false);
      toast.error(e?.message ?? t("pages.settings.deleteError"));
    }
  };

  const [profile, setProfile] = useState({
    first_name: "", last_name: "", firm_name: "", bar_order: "", bar_number: "", phone: "", pec: "",
  });
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState({ next: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return setLoading(false);
      setEmail(u.user.email ?? "");
      const { data } = await supabase.from("profiles").select("*").eq("id", u.user.id).maybeSingle();
      if (data) setProfile({
        first_name: data.first_name ?? "",
        last_name: data.last_name ?? "",
        firm_name: data.firm_name ?? "",
        bar_order: data.bar_order ?? "",
        bar_number: data.bar_number ?? "",
        phone: data.phone ?? "",
        pec: data.pec ?? "",
      });
      setLoading(false);
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) {
      setSaving(false);
      return;
    }
    const { error } = await supabase.from("profiles").update(profile).eq("id", u.user.id);
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success(t("pages.settings.profileUpdated"));
  };

  const changePassword = async () => {
    if (pw.next.length < 8) return toast.error(t("pages.settings.passwordTooShort"));
    if (pw.next !== pw.confirm) return toast.error(t("pages.settings.passwordsMismatch"));
    setPwSaving(true);
    const { error } = await supabase.auth.updateUser({ password: pw.next });
    setPwSaving(false);
    if (error) return toast.error(error.message);
    setPw({ next: "", confirm: "" });
    toast.success(t("pages.settings.passwordUpdated"));
  };

  if (loading) {
    return (
      <>
        <PageHeader title={t("pages.settings.title")} description={t("pages.settings.description")} />
        <PageBody>
          <Card className="flex items-center justify-center p-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </Card>
        </PageBody>
      </>
    );
  }

  return (
    <>
      <PageHeader title={t("pages.settings.title")} description={t("pages.settings.description")} />
      <PageBody>
        <div className="max-w-2xl space-y-6">
          <Card className="p-6">
            <div className="flex items-center gap-2">
              <UserIcon className="h-4 w-4 text-muted-foreground" />
              <h2 className="font-display text-lg font-semibold">{t("pages.settings.profileSection")}</h2>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">{email}</p>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div><Label>{t("pages.settings.fName")}</Label><Input value={profile.first_name} onChange={(e) => setProfile({ ...profile, first_name: e.target.value })} /></div>
              <div><Label>{t("pages.settings.fLastName")}</Label><Input value={profile.last_name} onChange={(e) => setProfile({ ...profile, last_name: e.target.value })} /></div>
              <div className="sm:col-span-2"><Label>{t("pages.settings.fFirm")}</Label><Input value={profile.firm_name} onChange={(e) => setProfile({ ...profile, firm_name: e.target.value })} /></div>
              <div><Label>{t("pages.settings.fBarOrder")}</Label><Input value={profile.bar_order} onChange={(e) => setProfile({ ...profile, bar_order: e.target.value })} placeholder={t("pages.settings.fBarOrderPh")} /></div>
              <div><Label>{t("pages.settings.fBarNumber")}</Label><Input value={profile.bar_number} onChange={(e) => setProfile({ ...profile, bar_number: e.target.value })} /></div>
              <div><Label>{t("pages.settings.fPhone")}</Label><Input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} /></div>
              <div><Label>{t("pages.settings.fPec")}</Label><Input value={profile.pec} onChange={(e) => setProfile({ ...profile, pec: e.target.value })} /></div>
            </div>
            <Button className="mt-6" onClick={save} disabled={saving}>{saving ? t("pages.settings.saving") : t("pages.settings.saveChanges")}</Button>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-2">
              <KeyRound className="h-4 w-4 text-muted-foreground" />
              <h2 className="font-display text-lg font-semibold">{t("pages.settings.passwordSection")}</h2>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">{t("pages.settings.passwordHint")}</p>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <Label>{t("pages.settings.newPassword")}</Label>
                <Input type="password" autoComplete="new-password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} />
              </div>
              <div>
                <Label>{t("pages.settings.confirmPassword")}</Label>
                <Input type="password" autoComplete="new-password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} />
              </div>
            </div>
            <Button className="mt-6" onClick={changePassword} disabled={pwSaving || !pw.next}>
              {pwSaving ? t("pages.settings.updating") : t("pages.settings.updatePassword")}
            </Button>
          </Card>

          <Card className="p-6 border-destructive/30">
            <div className="flex items-center gap-2">
              <Trash2 className="h-4 w-4 text-destructive" />
              <h2 className="font-display text-lg font-semibold">{t("pages.settings.deleteSection")}</h2>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("pages.settings.deleteDesc")}
            </p>
            <Button variant="destructive" className="mt-4" onClick={onDelete} disabled={deleting}>
              {deleting ? t("pages.settings.deleting") : t("pages.settings.deleteCta")}
            </Button>
          </Card>
        </div>
      </PageBody>
    </>
  );
}
